import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { UncategorizedReport } from '../components/UncategorizedReport';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FracionamentoContent } from './FracionamentoPage';
import { InventoryContent } from './InventoryPage';
import { LossesContent } from '../components/LossesContent';
import { ABCAnalysis } from '../components/ABCAnalysis';
import axios from 'axios';
import { Plus, Edit, Trash2, AlertCircle, TrendingUp, Beef, Package, Boxes, AlertOctagon, BarChart3, Upload, Download, FileSpreadsheet, CheckCircle2, Tag } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { overlayClose } from '../lib/overlayClose';
import { useListKeyboardNav } from '../lib/useListKeyboardNav';
import { useProductCategories, categoryLabelOf } from '../lib/categories';
import { NewCategoryButton } from '../components/NewCategoryButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductsPage = () => {
  const { user } = useAuth();
  const canFracionamento = hasPermission(user, 'fracionamento.manage');
  const [mainTab, setMainTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const categories = useProductCategories();
  const [formData, setFormData] = useState({
    name: '',
    category: 'mercearia',
    code: '',
    cost_price: '',
    price: '',
    unit: 'un',
    stock_quantity: '',
    min_stock: '',
    barcode: '',
    description: '',
    ncm: '',
    cest: '',
    cfop: '', csosn: '', cst: '', origem: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Importação de produtos (planilha)
  const [showImport, setShowImport] = useState(false);
  const [showUncategorized, setShowUncategorized] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');

  const handleImport = async (e) => {
    e.preventDefault();
    setImportError(''); setImportResult(null);
    if (!importFile) { setImportError('Selecione um arquivo .xlsx ou .csv.'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('skip_duplicates', 'true');
      const { data } = await axios.post(`${API}/products/import`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(data);
      await loadProducts();
    } catch (err) {
      setImportError(err.response?.data?.detail || 'Falha ao importar. Verifique o arquivo e tente novamente.');
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(data);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        cost_price: parseFloat(formData.cost_price) || 0,
        price: parseFloat(formData.price),
        stock_quantity: parseFloat(formData.stock_quantity),
        min_stock: parseFloat(formData.min_stock)
      };

      if (editingId) {
        await axios.put(`${API}/products/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/products`, payload, { withCredentials: true });
      }

      loadProducts();
      resetForm();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao salvar produto');
    } finally {
      setLoading(false);
    }
  };

  const [suggestedCode, setSuggestedCode] = useState(null);

  // Novo produto: sugere o próximo código interno livre
  const openNewProduct = async () => {
    if (showForm) { resetForm(); return; }
    setEditingId(null);
    setShowForm(true);
    try {
      const { data } = await axios.get(`${API}/products/next-code`, { withCredentials: true });
      setSuggestedCode(data);
      setFormData((f) => (f.code ? f : { ...f, code: data.next_code }));
    } catch (_e) { setSuggestedCode(null); }
  };

  const handleEdit = (product) => {
    setSuggestedCode(null);
    setFormData({
      name: product.name,
      category: product.category,
      code: product.code || '',
      cost_price: (product.cost_price || 0).toString(),
      price: product.price.toString(),
      unit: product.unit,
      stock_quantity: product.stock_quantity.toString(),
      min_stock: product.min_stock.toString(),
      barcode: product.barcode || '',
      description: product.description || '',
      ncm: product.ncm || '',
      cest: product.cest || '',
      cfop: product.cfop || '', csosn: product.csosn || '', cst: product.cst || '', origem: product.origem || ''
    });
    setEditingId(product.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;

    try {
      await axios.delete(`${API}/products/${id}`, { withCredentials: true });
      loadProducts();
    } catch (error) {
      setError('Erro ao excluir produto');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'mercearia',
      code: '',
      cost_price: '',
      price: '',
      unit: 'un',
      stock_quantity: '',
      min_stock: '',
      barcode: '',
      description: '',
      ncm: '',
      cest: '',
      cfop: '', csosn: '', cst: '', origem: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getCategoryColor = (category) => {
    const colors = {
      acougue: 'bg-red-100 text-red-600',
      bebidas: 'bg-blue-100 text-blue-600',
      mercearia: 'bg-yellow-100 text-yellow-700',
      flv: 'bg-green-100 text-green-700',
      padaria: 'bg-orange-100 text-orange-700',
      frios_laticinios: 'bg-sky-100 text-sky-700',
      limpeza: 'bg-cyan-100 text-cyan-700',
      higiene: 'bg-fuchsia-100 text-fuchsia-700',
      outros: 'bg-slate-100 text-slate-600'
    };
    return colors[category] || colors.outros;
  };

  // Calcular lucro/margem em tempo real para preview no formulário
  const calculatedProfit = (parseFloat(formData.price) || 0) - (parseFloat(formData.cost_price) || 0);
  const calculatedMargin = parseFloat(formData.cost_price) > 0
    ? ((calculatedProfit / parseFloat(formData.cost_price)) * 100)
    : 0;

  const filteredProducts = products.filter(p =>
    !search ||
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.barcode && p.barcode.includes(search)) ||
    (p.code && p.code.includes(search))
  );
  // Renderiza no máximo 100 linhas por vez — mantém a digitação instantânea mesmo
  // com milhares de produtos. A busca continua percorrendo o catálogo inteiro.
  const RENDER_LIMIT = 100;
  const visibleProducts = filteredProducts.slice(0, RENDER_LIMIT);
  const hiddenCount = filteredProducts.length - visibleProducts.length;
  const nav = useListKeyboardNav({ count: visibleProducts.length, resetKey: search, onSelect: (i) => handleEdit(visibleProducts[i]) });

  return (
    <Layout>
      <div className="p-8" data-testid="products-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Produtos</h1>
        </div>

        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList className={`grid w-full ${canFracionamento ? 'md:w-[820px] grid-cols-5' : 'md:w-[680px] grid-cols-4'} mb-6`}>
            <TabsTrigger value="products" data-testid="main-tab-products">
              <Package size={16} className="mr-2" /> Produtos
            </TabsTrigger>
            <TabsTrigger value="inventory" data-testid="main-tab-inventory">
              <Boxes size={16} className="mr-2" /> Estoque
            </TabsTrigger>
            <TabsTrigger value="abc" data-testid="main-tab-abc">
              <BarChart3 size={16} className="mr-2" /> Curva ABC
            </TabsTrigger>
            <TabsTrigger value="losses" data-testid="main-tab-losses">
              <AlertOctagon size={16} className="mr-2" /> Quebras
            </TabsTrigger>
            {canFracionamento && (
              <TabsTrigger value="fracionamento" data-testid="main-tab-fracionamento">
                <Beef size={16} className="mr-2" /> Fracionamento
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="products">
            <div className="flex items-center justify-end gap-2 mb-4">
              <Button
                variant="outline"
                onClick={() => setShowUncategorized(!showUncategorized)}
                className={showUncategorized ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-amber-300 text-amber-800 hover:bg-amber-50'}
                data-testid="uncategorized-toggle"
              >
                <Tag size={18} className="mr-2" />
                Sem categoria / Outros
              </Button>
              <Button
                variant="outline"
                onClick={() => { setShowImport(true); setImportResult(null); setImportError(''); setImportFile(null); }}
                className="border-slate-300"
                data-testid="import-products-button"
              >
                <Upload size={18} className="mr-2" />
                Importar
              </Button>
              <Button
                onClick={openNewProduct}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-product-button"
              >
                <Plus size={20} className="mr-2" />
                Novo Produto
              </Button>
            </div>

            {showUncategorized && <UncategorizedReport onChanged={loadProducts} />}

            {showImport && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="import-modal" {...overlayClose(() => !importing && setShowImport(false))}>
                <div className="bg-white rounded-lg border border-slate-200 w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2 mb-1">
                    <FileSpreadsheet className="text-blue-600" size={22} />
                    <h2 className="text-2xl font-bold text-slate-900">Importar produtos</h2>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">Envie uma planilha <b>.xlsx</b> ou <b>.csv</b> com os produtos. Colunas: nome, categoria, preco_venda, preco_custo, estoque_inicial, unidade, codigo_barras.</p>

                  <div className="flex flex-wrap gap-3 mb-4">
                    <a href="/modelo_importacao_produtos.xlsx" download className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium" data-testid="download-template-link">
                      <Download size={16} className="mr-1" /> Baixar modelo de planilha
                    </a>
                  </div>

                  {!importResult && (
                    <form onSubmit={handleImport} className="space-y-4">
                      <Input
                        type="file"
                        accept=".xlsx,.csv"
                        onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                        data-testid="import-file-input"
                        className="cursor-pointer"
                      />
                      {importError && (
                        <Alert variant="destructive" data-testid="import-error"><AlertDescription>{importError}</AlertDescription></Alert>
                      )}
                      <div className="flex gap-2 justify-end">
                        <Button type="button" variant="outline" onClick={() => setShowImport(false)} disabled={importing}>Cancelar</Button>
                        <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={importing} data-testid="import-submit">
                          {importing ? 'Importando...' : (<><Upload size={18} className="mr-2" />Importar</>)}
                        </Button>
                      </div>
                    </form>
                  )}

                  {importResult && (
                    <div className="space-y-3" data-testid="import-result">
                      <Alert className="bg-emerald-50 border-emerald-200 text-emerald-800">
                        <AlertDescription className="flex items-center gap-2">
                          <CheckCircle2 size={18} /> <span><b>{importResult.created}</b> produtos importados. {importResult.skipped > 0 && <>{importResult.skipped} ignorados (duplicados). </>}{importResult.error_count > 0 && <>{importResult.error_count} com erro.</>}</span>
                        </AlertDescription>
                      </Alert>
                      {importResult.errors && importResult.errors.length > 0 && (
                        <div className="max-h-40 overflow-y-auto text-xs text-slate-600 border border-slate-200 rounded p-2 space-y-1">
                          {importResult.errors.map((er, i) => <div key={i}>{er}</div>)}
                        </div>
                      )}
                      <div className="flex justify-end">
                        <Button onClick={() => { setShowImport(false); setImportResult(null); setImportFile(null); }} className="bg-blue-600 hover:bg-blue-700" data-testid="import-done">Concluir</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

        <div className="mb-6">
          <Input
            placeholder="Buscar por nome ou código de barras..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={nav.onKeyDown}
            title="↑ ↓ navegam na lista · Enter abre o produto"

            data-testid="search-products"
          />
        </div>

        {showForm && (
          <div className="bg-white rounded-md border border-slate-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {editingId ? 'Editar Produto' : 'Novo Produto'}
            </h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>

              <div className="space-y-2">
                <Label>Categoria *</Label>
                <div className="flex items-start gap-2">
                  {/* Radix dispara onValueChange('') quando a lista de itens muda; ignoramos valor vazio */}
                  <Select value={formData.category} onValueChange={(value) => { if (value) setFormData((f) => ({ ...f, category: value })); }}>
                    <SelectTrigger data-testid="product-category-select" data-category={formData.category || ""}><SelectValue>{categoryLabelOf(formData.category)}</SelectValue></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.value} value={c.value} data-testid={`product-category-opt-${c.value}`}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <NewCategoryButton onCreated={(value) => setFormData((f) => ({ ...f, category: value }))} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Preço de Custo (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cost_price}
                  onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label>Preço de Venda (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>

              {/* Preview de lucro */}
              {(formData.price && formData.cost_price) && (
                <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={20} className="text-blue-600" />
                    <span className="font-medium text-slate-700">Lucro Estimado:</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-green-600 text-lg">R$ {calculatedProfit.toFixed(2)}</div>
                    <div className="text-sm text-slate-600">Margem: <strong>{calculatedMargin.toFixed(2)}%</strong></div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Unidade *</Label>
                <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="un">Un</SelectItem>
                    <SelectItem value="lt">Lt</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Código Interno (curto)</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Ex: 10 (para usar no PDV: 1,356*10)"
                  data-testid="product-field-code"
                />
                {!editingId && suggestedCode && (
                  <p className="text-[11px] text-emerald-700" data-testid="product-code-suggestion">
                    Sugestão: <b>{suggestedCode.next_code}</b> é o próximo código livre{suggestedCode.last_code ? ` (último cadastrado: ${suggestedCode.last_code})` : ''}.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Código de Barras</Label>
                <Input
                  value={formData.barcode}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                  placeholder="Código EAN-13 ou da balança"
                />
              </div>

              <div className="space-y-2">
                <Label>Estoque Atual *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Estoque Mínimo *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.min_stock}
                  onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Descrição</Label>
                <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>

              <div className="space-y-2 md:col-span-2">
                <div className="bg-blue-50 border border-blue-200 rounded p-3 -mb-2">
                  <p className="text-xs font-bold text-blue-900 uppercase tracking-wider">Dados Fiscais (NFCe / SEFAZ)</p>
                  <p className="text-[10px] text-blue-700">Opcional. Necessário para emissão NFCe — pode preencher depois.</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>NCM (8 dígitos)</Label>
                <Input
                  value={formData.ncm}
                  onChange={(e) => setFormData({ ...formData, ncm: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                  placeholder="01012100"
                  maxLength={8}
                  data-testid="product-field-ncm"
                />
              </div>

              <div className="space-y-2">
                <Label>CEST (7 dígitos)</Label>
                <Input
                  value={formData.cest}
                  onChange={(e) => setFormData({ ...formData, cest: e.target.value.replace(/\D/g, '').slice(0, 7) })}
                  placeholder="0100100"
                  maxLength={7}
                  data-testid="product-field-cest"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">CFOP <span className="text-slate-400">(vazio = padrão da categoria)</span></Label>
                <Input value={formData.cfop} onChange={(e) => setFormData({ ...formData, cfop: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="5102" data-testid="product-field-cfop" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">CSOSN (Simples) / CST</Label>
                <div className="flex gap-1">
                  <Input value={formData.csosn} onChange={(e) => setFormData({ ...formData, csosn: e.target.value.replace(/\D/g, '').slice(0, 3) })} placeholder="102" data-testid="product-field-csosn" />
                  <Input value={formData.cst} onChange={(e) => setFormData({ ...formData, cst: e.target.value.replace(/\D/g, '').slice(0, 2) })} placeholder="CST 00" data-testid="product-field-cst" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Origem da mercadoria</Label>
                <select value={formData.origem} onChange={(e) => setFormData({ ...formData, origem: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="product-field-origem">
                  <option value="">Padrão da categoria</option>
                  <option value="0">0 — Nacional</option>
                  <option value="1">1 — Estrangeira (importação direta)</option>
                  <option value="2">2 — Estrangeira (mercado interno)</option>
                </select>
              </div>

              {error && (
                <div className="md:col-span-2">
                  <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
                </div>
              )}

              <div className="md:col-span-2 flex gap-2">
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Produto</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Categoria</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Custo</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Venda</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Lucro</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Margem</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Estoque</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {visibleProducts.map((product, idx) => (
                  <tr key={product.id} {...nav.rowProps(idx, 'hover:bg-slate-50 transition-colors')}>
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-slate-900">
                          {product.code && <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded mr-2">#{product.code}</span>}
                          {product.name}
                        </div>
                        {product.barcode && <div className="text-xs text-slate-500">{product.barcode}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${getCategoryColor(product.category)}`}>
                        {categoryLabelOf(product.category).split(' (')[0]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-slate-600">
                      R$ {(product.cost_price || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-bold text-blue-600">R$ {product.price.toFixed(2)}</div>
                      <div className="text-xs text-slate-500">por {product.unit}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${(product.profit || 0) > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                        R$ {(product.profit || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-medium ${(product.profit_margin || 0) > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                        {(product.profit_margin || 0).toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{product.stock_quantity} {product.unit}</span>
                        {product.stock_quantity <= product.min_stock && (
                          <AlertCircle size={16} className="text-red-600" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleEdit(product)} className="text-blue-600 hover:text-blue-700" data-testid={`edit-product-${product.id}`}>
                          <Edit size={18} />
                        </button>
                        <button onClick={() => handleDelete(product.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-product-${product.id}`}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hiddenCount > 0 && (
              <div className="px-4 py-3 text-center text-sm text-slate-500 bg-slate-50 border-t border-slate-200" data-testid="products-render-hint">
                Mostrando {visibleProducts.length} de {filteredProducts.length} produtos. Use a busca para encontrar itens específicos.
              </div>
            )}
          </div>
        </div>
          </TabsContent>

          <TabsContent value="inventory">
            <InventoryContent />
          </TabsContent>

          <TabsContent value="abc">
            <ABCAnalysis />
          </TabsContent>

          <TabsContent value="losses">
            <LossesContent />
          </TabsContent>

          {canFracionamento && (
            <TabsContent value="fracionamento">
              <FracionamentoContent />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
};
